import _extends from "@babel/runtime/helpers/esm/extends";
/**
 * MuiButtonBase
 */
export const MuiButtonBase = {
  defaultProps: {
    disableRipple: true
  }
};

/**
 * MuiButton
 *
 * Demos:
 *
 * - [Button](https://ui-packages.isbuildingluma.com/ui-core/react-button/)
 *
 * API:
 *
 * - [Button API](https://ui-packages.isbuildingluma.com/ui-core/api/button/)
 * - inherits [MuiButton API](https://mui.com/material-ui/api/button/)
 *
 * Styles:
 *
 * - [Proto](https://www.figma.com/proto/WJDRbOCCrGVDTsbUC3yvp8/Design-System-Playground?page-id=1%3A2&type=design&node-id=184-10512&viewport=609%2C310%2C0.08&scaling=min-zoom&starting-point-node-id=184%3A10512)
 */
const MuiButton = {
  defaultProps: {
    disableFocusRipple: true
  },
  styleOverrides: {
    root: ({
      ownerState,
      theme
    }) => _extends({
      textTransform: 'none',
      borderRadius: 24,
      '&.MuiButton-sizeLarge': {
        paddingTop: 10,
        paddingBottom: 10,
        lineHeight: theme.typography.pxToRem(20)
      }
    }, ownerState.variant === 'contained' && ownerState.color === 'primary' && {
      boxShadow: `${theme.shadows[1]} !important`,
      '&:hover': {
        backgroundColor: theme.palette.primary.light
      },
      '&:active': {
        backgroundColor: theme.palette.primary.dark
      },
      '&.Mui-focusVisible': {
        outline: `4px solid ${theme.palette.tertiary.light}`,
        backgroundColor: theme.palette.primary.light
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        opacity: 0.4
      }
    }, ownerState.variant === 'contained' && ownerState.color === 'inherit' && {
      backgroundColor: theme.palette.common.white,
      boxShadow: `${theme.shadows[1]} !important`,
      '&:hover': {
        backgroundColor: theme.palette.primary[200]
      },
      '&:active': {
        backgroundColor: theme.palette.primary[300]
      },
      '&.Mui-focusVisible': {
        outline: `4px solid ${theme.palette.tertiary.light}`,
        backgroundColor: theme.palette.primary[100]
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.black,
        opacity: 0.4
      }
    }, ownerState.variant === 'contained' && ownerState.color === 'error' && {
      boxShadow: `${theme.shadows[1]} !important`,
      '&:hover': {
        backgroundColor: theme.palette.error[600]
      },
      '&:active': {
        backgroundColor: theme.palette.error[700]
      },
      '&.Mui-focusVisible': {
        outline: `4px solid ${theme.palette.tertiary.light}`,
        backgroundColor: theme.palette.error[600]
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.error[500],
        color: theme.palette.error.contrastText,
        opacity: 0.4
      }
    }, ownerState.variant === 'text' && ownerState.color === 'inherit' && {
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: theme.palette.primary[200]
      },
      '&:active': {
        backgroundColor: theme.palette.primary[300]
      },
      '&.Mui-focusVisible': {
        outline: `4px solid ${theme.palette.tertiary.light}`,
        backgroundColor: theme.palette.primary[100]
      }
    }),
    sizeLarge: ({
      theme
    }) => ({
      fontSize: theme.typography.pxToRem(14),
      padding: theme.spacing(1.25, 2)
    }),
    startIcon: {
      '& .MuiSvgIcon-root': {
        fontSize: 16
      }
    },
    endIcon: {
      '& .MuiSvgIcon-root': {
        fontSize: 16
      }
    }
  }
};

/**
 * MuiFab
 *
 * Demos:
 *
 * - [Button](https://ui-packages.isbuildingluma.com/ui-core/react-button/)
 *
 * API:
 *
 * - [Fab API](https://ui-packages.isbuildingluma.com/ui-core/api/fab/)
 * - inherits [MuiFab API](https://mui.com/material-ui/api/fab/)
 *
 * Styles:
 *
 * - [Proto](https://www.figma.com/proto/WJDRbOCCrGVDTsbUC3yvp8/Design-System-Playground?page-id=1%3A2&type=design&node-id=184-10512&viewport=609%2C310%2C0.08&scaling=min-zoom&starting-point-node-id=184%3A10512)
 */
export const MuiFab = {
  defaultProps: {
    disableFocusRipple: true
  },
  styleOverrides: {
    root: ({
      ownerState,
      theme
    }) => _extends({}, ownerState.color === 'primary' && {
      boxShadow: `${theme.shadows[1]} !important`,
      '&:hover': {
        backgroundColor: theme.palette.primary.light
      },
      '&:active': {
        backgroundColor: theme.palette.primary.dark
      },
      '&.Mui-focusVisible': {
        outline: `4px solid ${theme.palette.tertiary.light}`,
        backgroundColor: theme.palette.primary.light
      }
    }, ownerState.color === 'secondary' && {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
      boxShadow: `${theme.shadows[1]} !important`,
      '&:hover': {
        backgroundColor: theme.palette.primary[200]
      },
      '&:active': {
        backgroundColor: theme.palette.primary[300]
      },
      '&.Mui-focusVisible': {
        outline: `4px solid ${theme.palette.tertiary.light}`,
        backgroundColor: theme.palette.primary[100]
      }
    }, ownerState.color === 'error' && {
      boxShadow: `${theme.shadows[1]} !important`,
      '&:hover': {
        backgroundColor: theme.palette.error[600]
      },
      '&:active': {
        backgroundColor: theme.palette.error[700]
      },
      '&.Mui-focusVisible': {
        outline: `4px solid ${theme.palette.tertiary.light}`,
        backgroundColor: theme.palette.error[600]
      }
    })
  }
};
export const MuiButtonGroup = {
  defaultProps: {
    disableRipple: true,
    disableFocusRipple: true
  },
  styleOverrides: {
    root: ({
      theme
    }) => ({
      boxShadow: 'none',
      '& .MuiButtonGroup-grouped:not(:last-of-type)': {
        borderColor: theme.palette.primary[300],
        '&.MuiButton-containedPrimary': {
          borderColor: theme.palette.primary.light
        },
        '&.MuiButton-containedError': {
          borderColor: theme.palette.error.light
        }
      }
    })
  }
};
export default MuiButton;